import React, { useContext, useState } from 'react'
import ReactGA from 'react-ga'
import { Plural, t, Trans } from '@lingui/macro'

import {
  Box,
  Flex,
  ButtonPrimary,
  BackButton,
  ButtonOutline,
  ButtonGroup,
  Text,
  Tooltip
} from 'front-end/src/components'

import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from '../../../../app/util/appHistory'
import { TopicEnrolmentRestartModal } from '../../TopicEnrolmentRestartModal'
import { TopicPageMode } from '../../TopicDetailPage'
import { TopbarDrawer } from '../drawers/TopbarDrawer'

import { brandColors } from 'shared/basicPalette'
import { dayjsFromNow } from 'shared/dayjs'
import { NotificationMessage } from './NotificationMessage'
import { TopicDetailContext } from '../../TopicDetailContext'
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch
} from 'react-router-dom'
import IconPencil from '../../../../components/common/icon/IconPencil'
import { IconChatText } from '../../../../components/common/icon/IconChatText'
import { IconVersions } from '../../../../components/common/icon/IconVersions'
import { ResourceFeedbackDrawerContent } from '../drawers/resource-feedback-drawer/ResourceFeedbackDrawerContent'
import { ResourceVersionsDrawerContent } from '../drawers/resource-versions-drawer/ResourceVersionsDrawerContent'
import {
  Topbar,
  TopbarCenter,
  TopbarLeft,
  TopbarRight,
  TopbarTitle
} from 'front-end/src/components/common/topbar/Topbar'
import { RetryResourceQuiz } from '../../RetryResourceQuiz'
import { HideOnMobileDevice } from '../../../../components/common/HideOnMobileDevice'
import { videoAssessmentTopicVersionTypesForFE } from '../../StudentTopicDetail'
import { IconCamera } from '../../../../components/common/icon/IconCamera'
import { Tab, TabList, Tabs } from '@chakra-ui/react'
import { TopicVersionType } from 'front-end/src/graphql/private/PrivateSchemaTypes'

enum DrawerContentEnum {
  feedback = 'feedback',
  version = 'version'
}

export const TopbarView = () => {
  const { search } = useLocation()
  const history = useHistory()
  const params = useParams<{
    feedbackTopicEnrolmentId: string
  }>()
  const { topic, mode, topicVersion } = useContext(TopicDetailContext)
  const [showFeedbackVersionDrawer, setShowFeedbackVersionDrawer] = useState(
    !!params.feedbackTopicEnrolmentId || search.includes('showVersionsDrawer')
  )
  const [drawerContent, setDrawerContent] = useState<DrawerContentEnum | null>(
    params.feedbackTopicEnrolmentId
      ? DrawerContentEnum.feedback
      : search.includes('showVersionsDrawer')
      ? DrawerContentEnum.version
      : null
  )
  const [showRestartProgress, setShowRestartProgress] = useState<boolean>(false)
  const matchRoute = useRouteMatch<{ parentPath: string }>({
    path: ':parentPath(.*)?/topic/:topicId/(.*)?',
    exact: true
  })
  const defaultBackButtonPath = matchRoute?.params?.parentPath

  const enrolment = topic.enrolmentOrSelfEnrol

  const availablePoints = enrolment?.available_points as number
  const passMark = enrolment?.pass_mark as any
  const correctPoints = enrolment?.auto_correct as number
  const passMarkPassed = passMark > 0 && correctPoints >= passMark
  const hasQuiz = availablePoints > 0 && !!passMark
  const hasQuizAndFailed = hasQuiz && !passMarkPassed
  const editPrivileges = topic.__typename === 'TopicModelAsEditor'
  const notificationMessageCode =
    topic.enrolmentOrSelfEnrol?.notificationMessage?.message_code

  const isVideoAssessment =
    topicVersion?.topic_type &&
    videoAssessmentTopicVersionTypesForFE.includes(topicVersion?.topic_type)

  const isScorm = topicVersion?.topic_type === TopicVersionType.scorm
  const isEnrolmentCompleted = !!enrolment?.completion_date
  const maxAttempts = topic.currentTopicVersion?.max_attempts
  const currentAttempt = topic.enrolmentOrSelfEnrol?.attempt || 1
  const attemptsLeft = maxAttempts
    ? maxAttempts - (isEnrolmentCompleted ? currentAttempt : currentAttempt - 1)
    : null

  const renderNotificationMessage = () => {
    if (
      notificationMessageCode &&
      !topic?.enrolmentOrSelfEnrol?.canBeRestarted
    ) {
      return <NotificationMessage messageCode={notificationMessageCode} />
    }
  }

  const renderAttemptsLeft = () => {
    if ((hasQuiz || isScorm) && !passMarkPassed) {
      if (!attemptsLeft || attemptsLeft < 0) {
        return null
      }
      return (
        <Box mt="2px" color={brandColors.grey.mid}>
          <Plural
            value={attemptsLeft}
            one="# attempt left"
            other="# attempts left"
          />
        </Box>
      )
    }
  }

  const renderCenterPanel = () => {
    if (enrolment?.completion_date) {
      return (
        <>
          <Flex
            color={brandColors.grey.mid}
            fontSize={['xs', 'sm']}
            flexDirection="column"
            alignItems="flex-end"
            py="4px"
            mr="8px"
            lineHeight="1.3em"
          >
            <Box>
              {hasQuizAndFailed ? (
                <>
                  <Trans>Failed</Trans>{' '}
                  {dayjsFromNow(enrolment.completion_date)}
                </>
              ) : (
                <>
                  <Trans>Completed</Trans>{' '}
                  {dayjsFromNow(enrolment.completion_date)}
                </>
              )}
            </Box>
            {topic.enrolmentOrSelfEnrol?.canBeRestarted && (
              <Text color={brandColors.orange['600']} fontWeight="bold">
                <Trans>Newer version available</Trans>
              </Text>
            )}
            {renderAttemptsLeft()}
          </Flex>
          {hasQuiz && (attemptsLeft === null || attemptsLeft > 0) && (
            <>
              {!passMarkPassed && topic.currentTopicVersion?.allow_retry && (
                <RetryResourceQuiz
                  topicId={topic.id}
                  enrolmentId={topic.enrolmentOrSelfEnrol?.id}
                  isScorm={isScorm}
                />
              )}
            </>
          )}

          {topic.enrolmentOrSelfEnrol?.canBeRestarted && (
            <ButtonPrimary
              onClick={async () => {
                ReactGA.event({
                  category: 'Resources / Re-enrol / top-bar',
                  action: 'Click Get Latest version',
                  label: 'Resource Quiz',
                  value: 0.5
                })
                setShowRestartProgress(true)
              }}
              dataTest="GetLatestVersionButton"
            >
              <Trans>Get latest version</Trans>
            </ButtonPrimary>
          )}
        </>
      )
    } else {
      return renderAttemptsLeft()
    }
  }

  if (!topicVersion) {
    return null
  }

  const toggleDrawer = (dc?: DrawerContentEnum) => {
    if (dc && dc !== drawerContent) {
      !showFeedbackVersionDrawer && setShowFeedbackVersionDrawer(true)
      setDrawerContent(dc)
    } else {
      if (search.includes('showVersionsDrawer')) {
        history.replace(location.pathname)
      }
      setShowFeedbackVersionDrawer(false)
      setDrawerContent(null)
    }
  }

  return (
    <>
      <Topbar>
        <TopbarLeft w="100%" maxW={['100%', '100%', '50%']}>
          <BackButton
            mr="4px"
            defaultPath={
              defaultBackButtonPath || `/workspace/${topic?.workspace_id}`
            }
          >
            <Trans>Exit</Trans>
          </BackButton>

          <TopbarTitle>{topic.currentTitle}</TopbarTitle>
        </TopbarLeft>
        <TopbarCenter maxW={['25%', '25%', '50%']}>
          {renderNotificationMessage()}
          {renderCenterPanel()}
          {mode === TopicPageMode.Gradebook && (
            <Tabs size="lg">
              <TabList borderBottom={0}>
                <Tab
                  borderRadius={0}
                  isDisabled
                  onClick={() => navigateTo(linkBuilder.topicEditUrl(topic.id))}
                >
                  <IconCamera mr="8px" /> <Trans>Gradebook</Trans>
                </Tab>
              </TabList>
            </Tabs>
          )}
        </TopbarCenter>
        <TopbarRight flex={[0, 0, 1]}>
          <HideOnMobileDevice>
            {mode === TopicPageMode.VersionView && (
              <>
                <Flex align="center" mr="8px">
                  <Trans>Viewing</Trans>&nbsp;
                  <strong>
                    <Trans>Ver.</Trans>
                  </strong>
                  &nbsp;
                  <strong>{topicVersion.version}</strong>
                </Flex>
                <ButtonOutline
                  dataTest="ViewLatestButton"
                  px="12px"
                  onClick={() => navigateTo(linkBuilder.topicUrl(topic.id))}
                >
                  <Trans>View latest</Trans>
                </ButtonOutline>
              </>
            )}

            {editPrivileges &&
              isVideoAssessment &&
              mode !== TopicPageMode.Gradebook && (
                <ButtonPrimary
                  ml="8px"
                  px="12px"
                  onClick={() =>
                    navigateTo(
                      linkBuilder.videoAssessmentGradeBookUrl(topic.id)
                    )
                  }
                  leftIcon={<IconCamera />}
                >
                  <Trans>Gradebook</Trans>
                </ButtonPrimary>
              )}

            {mode === TopicPageMode.Gradebook && (
              <ButtonOutline
                ml="8px"
                px="12px"
                onClick={() => {
                  navigateTo(linkBuilder.topicUrl(topic.id))
                }}
              >
                <Trans>Exit gradebook</Trans>
              </ButtonOutline>
            )}

            {editPrivileges && mode && (
              <>
                {mode !== TopicPageMode.VersionView &&
                  mode !== TopicPageMode.Gradebook &&
                  !topic.deleted_at && (
                    <ButtonGroup isAttached ml="8px">
                      <ButtonOutline
                        px="12px"
                        className="edit-button"
                        dataTest="EditButton"
                        onClick={() =>
                          navigateTo(linkBuilder.topicEditUrl(topic.id))
                        }
                        leftIcon={<IconPencil />}
                      >
                        <Trans>Edit</Trans>
                      </ButtonOutline>
                    </ButtonGroup>
                  )}

                <Tooltip
                  tabIndex={-1}
                  text={<Trans>Feedback</Trans>}
                  aria-label={t`Feedback`}
                >
                  <ButtonOutline
                    ml="8px"
                    dataTest="FeedbackButton"
                    aria-label={
                      showFeedbackVersionDrawer &&
                      drawerContent === DrawerContentEnum.feedback
                        ? t`Hide feedback`
                        : t`Show feedback`
                    }
                    iconOnly
                    onClick={() => toggleDrawer(DrawerContentEnum.feedback)}
                    isActive={drawerContent === DrawerContentEnum.feedback}
                  >
                    <IconChatText w="18px" h="17px" />
                  </ButtonOutline>
                </Tooltip>
                <Tooltip
                  tabIndex={-1}
                  text={<Trans>Versions</Trans>}
                  aria-label={t`Versions`}
                >
                  <ButtonOutline
                    ml="8px"
                    dataTest="VersionsButton"
                    aria-label={
                      showFeedbackVersionDrawer &&
                      drawerContent === DrawerContentEnum.version
                        ? t`Hide versions`
                        : t`Show versions`
                    }
                    iconOnly
                    onClick={() => toggleDrawer(DrawerContentEnum.version)}
                    isActive={drawerContent === DrawerContentEnum.version}
                  >
                    <IconVersions w="18px" h="16px" />
                  </ButtonOutline>
                </Tooltip>
              </>
            )}
          </HideOnMobileDevice>
        </TopbarRight>
      </Topbar>

      <TopbarDrawer
        show={showFeedbackVersionDrawer}
        onHide={() => toggleDrawer()}
        title={
          drawerContent === DrawerContentEnum.version
            ? t`Versions`
            : t`Feedback`
        }
      >
        {drawerContent === DrawerContentEnum.feedback && (
          <ResourceFeedbackDrawerContent topicId={topic.id} />
        )}
        {drawerContent === DrawerContentEnum.version && (
          <ResourceVersionsDrawerContent topicId={topic.id} />
        )}
      </TopbarDrawer>

      {showRestartProgress && (
        <TopicEnrolmentRestartModal
          positiveCta={<Trans>Get latest version</Trans>}
          negativeCta={<Trans>Cancel</Trans>}
          callback={() => {
            ReactGA.event({
              category: 'Resources / Re-enrol / modal',
              action: 'Click Get Latest version',
              label: 'Resource Quiz',
              value: 0.5
            })
            setShowRestartProgress(false)
          }}
          callbackCancel={() => {
            ReactGA.event({
              category: 'Resources / Re-enrol / modal',
              action: 'Click Cancel',
              label: 'Resource Quiz'
            })
            setShowRestartProgress(false)
          }}
          onHide={() => {
            ReactGA.event({
              category: 'Resources / Re-enrol / modal',
              action: 'Click Cancel (outside modal)',
              label: 'Resource Quiz'
            })
            setShowRestartProgress(false)
          }}
        />
      )}
    </>
  )
}
